import React, { useEffect, useState } from 'react';

import Link from 'next/link';

import analytics from '@repo/analytics';
import { TransformedUseCase } from '@repo/editor/src/types';
import { withEventScope } from '@repo/event-processor';

import { USECASES_SUB_USECASE } from 'global/analyticsEvents';

import {
  getMergedSubUseCases,
  USE_CASE_MINIMUM_TOTAL,
  TransformedSubUseCase,
  TRENDING_LISTS_LIMIT,
} from '../utils';

import { SubUseCaseChip, SubUseCaseChipText, Wrapper } from './styles';
import { SubUseCaseSectionProps } from './type';
import { buildSubUseCaseUrl, buildSubUseCaseUrlToggleUrl } from './utils';

const SubUseCaseSection: React.FC<SubUseCaseSectionProps> = ({
  allUseCases,
  currentSelection,
}) => {
  const {
    useCaseSlug: currentUseCaseSlug,
    subUseCaseSlug: currentSubUseCaseSlug,
    categorySlug: currentCategorySlug,
    subCategorySlug: currentSubCategorySlug,
  } = currentSelection;

  const [subUseCases, setSubUseCases] = useState<TransformedSubUseCase[]>([]);

  useEffect(() => {
    const mergedSubUseCases = getMergedSubUseCases(
      allUseCases,
      currentCategorySlug,
      currentSubCategorySlug,
      currentUseCaseSlug
    );

    // Filter out use cases with less than 5 templates and sort by popularity
    const filteredSubUseCases = mergedSubUseCases
      .filter(
        (useCase) => !useCase.hidden && useCase.total >= USE_CASE_MINIMUM_TOTAL
      )
      .sort((a, b) => b.popularity - a.popularity)
      .slice(0, TRENDING_LISTS_LIMIT);

    setSubUseCases(filteredSubUseCases);
  }, [
    allUseCases,
    currentCategorySlug,
    currentSubCategorySlug,
    currentUseCaseSlug,
  ]);

  const handleUseCaseClick = ({
    name,
    slug,
  }: Pick<TransformedUseCase, 'name' | 'slug'>): void => {
    void analytics.track(USECASES_SUB_USECASE, { name, slug });
  };

  return (
    <Wrapper showBorder={!!subUseCases.length}>
      {subUseCases?.map((subUseCase) => {
        const isActive = subUseCase.slug === currentSubUseCaseSlug;
        return (
          <Link
            key={subUseCase.slug}
            data-testid={`usecases-subUseCase--${subUseCase.slug}`}
            href={
              isActive
                ? buildSubUseCaseUrlToggleUrl(currentSelection)
                : buildSubUseCaseUrl(currentSelection, subUseCase)
            }
            onClick={(): void => handleUseCaseClick(subUseCase)}
          >
            <SubUseCaseChip isActive={isActive}>
              <SubUseCaseChipText isActive={isActive}>
                {subUseCase.name}
              </SubUseCaseChipText>
            </SubUseCaseChip>
          </Link>
        );
      })}
    </Wrapper>
  );
};

export default withEventScope(SubUseCaseSection, {
  feature: 'subUseCaseSection',
});
